import React, { useState } from "react";
import axios from "axios";
import { MagnifyingGlass } from "@phosphor-icons/react";
import { AIOutput, ChipSelector } from "@/components/shared";
import { API } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const AITeaTuesday = () => {
  const { user } = useAuth();
  const industry = user?.industry || "AI & Technology";

  const [form, setForm] = useState({
    topic_mode: "search",
    custom_topic: "",
    format_length: "short-reel",
    platform: "instagram",
  });
  const [newsItems, setNewsItems] = useState([]);
  const [selectedStoryIds, setSelectedStoryIds] = useState([]);
  const [newsIndustry, setNewsIndustry] = useState("");
  const [output, setOutput] = useState(null);
  const [loading, setLoading] = useState(false);
  const [searchingNews, setSearchingNews] = useState(false);

  const formatLengths = [
    { value: "short-reel", label: "Short Reel (30-60s)" },
    { value: "mid-length", label: "Mid (2-4min)" },
    { value: "long-form", label: "Long (8-12min)" },
    { value: "live", label: "Live (15-20min)" },
  ];

  const searchNews = async () => {
    setSearchingNews(true);
    try {
      const res = await axios.post(`${API}/ai-tea/search-news`);
      const items = res.data.items || [];
      setNewsItems(items);
      // Spec: user explicitly asked to "choose from" stories — start
      // with nothing pre-selected so the cards are an active pick, not
      // a passive deselect-the-ones-you-don't-want.
      setSelectedStoryIds([]);
      setNewsIndustry(res.data.industry || industry);
    } catch (e) {
      console.error("Error searching news:", e);
    } finally {
      setSearchingNews(false);
    }
  };

  const toggleStory = (id) => {
    setSelectedStoryIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  const generateScript = async () => {
    setLoading(true);
    try {
      const res = await axios.post(`${API}/ai-tea/generate`, {
        ...form,
        selected_story_ids: form.topic_mode === "search" ? selectedStoryIds : null,
      });
      setOutput(res.data.result);
    } catch (e) {
      console.error("Error generating script:", e);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold text-zinc-900">Industry News</h2>
        <p className="text-zinc-500 mt-1">
          Latest {industry} news — scripts powered by live search in your niche
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="space-y-6">
          <div className="card p-4 sm:p-6 space-y-4">
            <ChipSelector
              label="Topic Mode"
              options={[
                { value: "search", label: `Search Latest ${industry} News` },
                { value: "custom", label: "Custom Topic" },
              ]}
              selected={form.topic_mode}
              onChange={(v) => setForm({ ...form, topic_mode: v })}
            />

            {form.topic_mode === "search" ? (
              <div>
                <button
                  onClick={searchNews}
                  disabled={searchingNews}
                  className="btn-secondary w-full flex items-center justify-center gap-2"
                  data-testid="search-news-btn"
                >
                  {searchingNews ? (
                    <>
                      <div className="w-4 h-4 border-2 border-[#E91E8C] border-t-transparent rounded-full animate-spin"></div>
                      Searching {industry} news...
                    </>
                  ) : (
                    <>
                      <MagnifyingGlass size={18} />
                      Search Latest {industry} News
                    </>
                  )}
                </button>
                {newsItems.length > 0 && (
                  <div className="mt-4 space-y-2" data-testid="news-items-list">
                    <div className="flex items-center justify-between">
                      {newsIndustry && (
                        <p className="text-xs font-semibold text-[#E91E8C]">
                          {newsIndustry} · last 72h · {newsItems.length} stories
                        </p>
                      )}
                      <p className="text-[10px] text-zinc-500">
                        Selected: <span className="font-semibold text-zinc-700">{selectedStoryIds.length}</span> / {newsItems.length}
                      </p>
                    </div>
                    <div className="space-y-2 max-h-[440px] overflow-y-auto pr-1">
                      {newsItems.map((it) => {
                        const selected = selectedStoryIds.includes(it.id);
                        return (
                          <button
                            key={it.id}
                            type="button"
                            onClick={() => toggleStory(it.id)}
                            className={`w-full text-left p-3 rounded-lg border-2 transition-all ${
                              selected
                                ? "border-[#E91E8C] bg-[#E91E8C]/5"
                                : "border-zinc-200 hover:border-zinc-300 bg-white"
                            }`}
                            data-testid={`news-story-${it.id}`}
                          >
                            <div className="flex items-start gap-2">
                              <div
                                className={`mt-1 w-4 h-4 rounded border-2 flex-shrink-0 flex items-center justify-center ${
                                  selected ? "border-[#E91E8C] bg-[#E91E8C]" : "border-zinc-300"
                                }`}
                                aria-hidden
                              >
                                {selected && <span className="text-white text-[10px] leading-none">✓</span>}
                              </div>
                              <div className="flex-1 min-w-0">
                                <p className="text-sm font-bold text-zinc-900 leading-tight">{it.title}</p>
                                <div className="flex items-center gap-2 mt-0.5 text-[10px] text-zinc-500">
                                  {it.source && <span className="font-mono">{it.source}</span>}
                                  {it.published_date && <span>· {it.published_date.slice(0, 10)}</span>}
                                </div>
                                <p className="text-xs text-zinc-700 mt-1.5 leading-snug">{it.summary}</p>
                                {it.why_it_matters && (
                                  <p className="text-[11px] text-[#E91E8C] mt-1 italic leading-snug">
                                    💡 {it.why_it_matters}
                                  </p>
                                )}
                                {it.url && (
                                  <a
                                    href={it.url}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    onClick={(e) => e.stopPropagation()}
                                    className="text-[10px] text-zinc-400 hover:text-[#E91E8C] underline mt-1 inline-block"
                                  >
                                    Read source
                                  </a>
                                )}
                              </div>
                            </div>
                          </button>
                        );
                      })}
                    </div>
                  </div>
                )}
                {!searchingNews && newsItems.length === 0 && newsIndustry && (
                  <p className="text-xs text-zinc-500 mt-3 italic" data-testid="news-empty-state">
                    No recent {newsIndustry} stories in the last 72 hours. Try a custom topic.
                  </p>
                )}
              </div>
            ) : (
              <textarea
                value={form.custom_topic}
                onChange={(e) => setForm({ ...form, custom_topic: e.target.value })}
                className="textarea-field h-32"
                placeholder={`Enter a custom ${industry} topic or paste news...`}
                data-testid="custom-topic-input"
              />
            )}

            <ChipSelector label="Format" options={formatLengths} selected={form.format_length} onChange={(v) => setForm({ ...form, format_length: v })} />
            <ChipSelector
              label="Platform"
              options={["instagram", "tiktok", "linkedin", "youtube"]}
              selected={form.platform}
              onChange={(v) => setForm({ ...form, platform: v })}
            />

            <button onClick={generateScript} disabled={loading || (form.topic_mode === "search" && selectedStoryIds.length === 0)} className="btn-primary w-full" data-testid="generate-news-script-btn">
              {loading
                ? "Generating..."
                : form.topic_mode === "search" && selectedStoryIds.length > 0
                  ? `Generate Script from ${selectedStoryIds.length} ${selectedStoryIds.length === 1 ? "Story" : "Stories"}`
                  : form.topic_mode === "search"
                    ? "Pick at least one story above"
                    : `Generate ${industry} News Script`}
            </button>
          </div>
        </div>

        <AIOutput content={output} loading={loading} label={`Your ${industry} News Script`} />
      </div>
    </div>
  );
};

export default AITeaTuesday;
